/**
 * Authentication API endpoints.
 */

import { api } from './client';
import type { ApiResponse, AuthResponse, LoginRequest } from '../types';

/** Auth API */
export const authApi = {
  /** Login with email and password */
  login: (credentials: LoginRequest) =>
    api.post<ApiResponse<AuthResponse>>('/auth/login', credentials, { auth: 'none' }),

  /**
   * Register a new account.
   * Backend returns 200 (ok_empty) even for duplicate emails (anti-enumeration),
   * and 403 { error.code: "registration_closed" } when registration is disabled.
   */
  register: (params: { name: string; email: string; password: string; turnstileToken?: string; inviteToken?: string }) =>
    api.post<ApiResponse<null>>('/auth/register', {
      name: params.name,
      email: params.email,
      password: params.password,
      // AB#196: mirror the funnel member-subscribe field name so one backend
      // verifier handles both. Omitted entirely when no widget was rendered.
      ...(params.turnstileToken ? { 'cf-turnstile-response': params.turnstileToken } : {}),
      // AB#537: super-admin signup invite; lets this email register while closed.
      ...(params.inviteToken ? { invite_token: params.inviteToken } : {}),
    }),

  /** Verify an email address using the token from the verification email. */
  verifyEmail: (token: string) =>
    api.get<ApiResponse<null>>(`/auth/verify/${encodeURIComponent(token)}`, { auth: 'none' }),

  /** Resend the verification email (always 200, no-op if unknown/already verified). */
  resendVerification: (email: string) =>
    api.post<ApiResponse<null>>('/auth/resend-verification-mail', { email }),

  /** Request a password-reset email (always 200, anti-enumeration). */
  forgotPassword: (email: string) =>
    api.post<ApiResponse<null>>('/auth/forgot', { email }),

  /** Reset with the emailed token; invalid/expired tokens return reset_token_invalid. */
  resetPassword: (params: { token: string; password: string }) =>
    api.post<ApiResponse<null>>('/auth/reset', params, { auth: 'none' }),

  /** Get current user — validates the JWT (used for the boot auth check). */
  me: (token?: string) =>
    api.get<ApiResponse<{ pid: string; name: string; email: string }>>('/auth/current',
      token === undefined ? undefined : { auth: 'none', headers: { Authorization: `Bearer ${token}` } }),

  /** Update the current user's display name (AB#463); returns the refreshed user. */
  updateProfile: (params: { name: string }) =>
    api.post<ApiResponse<{ pid: string; name: string; email: string }>>('/auth/profile', params),

  /**
   * Change the current user's password (AB#463). The backend verifies
   * `current_password` first and rejects a wrong one with 400
   * `current_password_invalid` (loud, no silent no-op — AB#406 style).
   *
   * Password-change session revocation: the change invalidates EVERY previously
   * issued token (including this session's), so the response carries a fresh
   * token the caller must store to stay signed in.
   */
  changePassword: (params: { current_password: string; new_password: string }) =>
    api.post<ApiResponse<{ token: string; pid: string; name: string; is_verified: boolean }>>(
      '/auth/password',
      params,
    ),

  /** Logout (invalidate token) */
  logout: () =>
    api.post<ApiResponse<null>>('/auth/logout', {}),

  /** Refresh token */
  refresh: () =>
    api.post<ApiResponse<AuthResponse>>('/auth/refresh', {}),

  /** Get user's API key for MCP/programmatic access */
  getApiKey: () =>
    api.get<ApiResponse<{ api_key: string }>>('/auth/api-key'),

  /**
   * Social login availability (AB#333). `enabled` is false until the Supabase
   * GoTrue env is configured; the login page hides provider buttons then.
   * `url` is the GoTrue base (authorize at `{url}/auth/v1/authorize`).
   */
  socialStatus: () =>
    api.get<ApiResponse<{ enabled: boolean; url: string; providers: string[] }>>(
      '/auth/social/status',
    ),

  /**
   * Exchange a Supabase GoTrue access token for an app session (AB#333).
   * Response matches the normal login shape: { token, pid, name, is_verified }.
   */
  socialLogin: (accessToken: string) =>
    api.post<ApiResponse<{ token: string; pid: string; name: string; is_verified: boolean }>>(
      '/auth/social/login',
      { access_token: accessToken },
      { auth: 'none' },
    ),
};
